import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutDashboard, List, ChevronDown, X, Plus, MapIcon, Trash2, Pencil, Check, Filter, MoreVertical } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { sitemapService, DbSitemap } from '@/lib/sitemapService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';


interface SiteMapperProps {
  isMobile?: boolean;
}

export function SiteMapper({ isMobile = false }: SiteMapperProps) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<'none' | 'recent'>('none');
  const [filterBy, setFilterBy] = useState<'all' | 'archived'>('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [sitemaps, setSitemaps] = useState<DbSitemap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const userId = user?.id;
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  // Load sitemaps from Supabase
  const loadSitemaps = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const data = await sitemapService.getSitemapsByWorkspace(workspaceId);
      setSitemaps(data);
    } catch (err) {
      console.error('Failed to load sitemaps:', err);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { loadSitemaps(); }, [loadSitemaps]);

  const handleNewSitemap = async () => {
    if (!workspaceId || !userId) return;
    setIsCreating(true);
    try {
      const sitemap = await sitemapService.createSitemap(workspaceId, userId, 'Untitled Sitemap');
      navigate(`/brand/sitemaps/editor/${sitemap.id}?new=true`);
    } catch (err) {
      console.error('Failed to create sitemap:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await sitemapService.deleteSitemap(id);
      setSitemaps(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete sitemap:', err);
    }
  };

  const startRename = (s: DbSitemap) => {
    setRenamingId(s.id);
    setRenameValue(s.name);
  };

  const commitRename = async (id: string) => {
    if (!renameValue.trim()) return;
    try {
      await sitemapService.renameSitemap(id, renameValue.trim());
      setSitemaps(prev => prev.map(s => s.id === id ? { ...s, name: renameValue.trim() } : s));
    } catch (err) {
      console.error('Failed to rename sitemap:', err);
    } finally {
      setRenamingId(null);
    }
  };

  // Filter + sort sitemaps
  const displayedSitemaps = sitemaps
    .filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => sortBy === 'recent'
      ? new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      : 0
    );

  return (
    <div className={`w-full px-6 md:px-10 py-6`}>
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-foreground">Sitemaps</h1>
        </div>

        <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'hidden' : ''}`}>

          {/* Search — icon-only by default, expands on click */}
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="search-open"
                  className="relative flex items-center h-10"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search sitemaps..."
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* View Toggle — animated sliding pill */}
          <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-10">
            <motion.div
              className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
              initial={false}
              animate={{
                left: viewMode === 'grid' ? '4px' : 'calc(50%)',
                width: 'calc(50% - 4px)',
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            />
            <button
              onClick={() => setViewMode('grid')}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <LayoutDashboard className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Filter Dropdown */}
          <div className="relative h-10">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${filterBy !== 'all'
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
            >
              <span className="text-sm font-medium whitespace-nowrap">{filterBy === 'all' ? 'All Sitemaps' : 'Archived'}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {showFilterMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowFilterMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {(['all', 'archived'] as const).map((opt) => (
                      <button
                        key={opt}
                        onClick={() => { setFilterBy(opt); setShowFilterMenu(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${filterBy === opt
                          ? 'bg-background shadow-sm text-foreground'
                          : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <span>{opt === 'all' ? 'All Sitemaps' : 'Archived'}</span>
                        {filterBy === opt && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Sort Dropdown */}
          <div className="relative h-10">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className={`p-2.5 h-full rounded-lg border transition-colors ${sortBy !== 'none'
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
              title="Sort sitemaps"
            >
              <Filter className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[160px] p-1.5 z-40"
                  >
                    {(['none', 'recent'] as const).map((opt) => (
                      <button
                        key={opt}
                        onClick={() => { setSortBy(opt); setShowSortMenu(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${sortBy === opt
                          ? 'bg-background shadow-sm text-foreground'
                          : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <span>{opt === 'none' ? 'Default' : 'Most Recent'}</span>
                        {sortBy === opt && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={handleNewSitemap}
            disabled={isCreating}
            className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap disabled:opacity-60"
          >
            {isCreating ? <LoadingSpinner className="text-white" /> : <Plus className="w-5 h-5" />}
            <span>New Sitemap</span>
          </button>
        </div>
      </div>

      {/* Grid/List View */}
      {isLoading ? (
        null
      ) : displayedSitemaps.length === 0 ? (
        <div className="py-16">
          <div className="p-8 rounded-lg bg-board-column border-none shadow-sm flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 rounded-full mb-4 flex items-center justify-center bg-muted">
              <MapIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-xl font-semibold mb-2 text-foreground">No sitemaps created yet</p>
            <p className="text-base mb-6 text-center max-w-md text-muted-foreground">
              Create your first sitemap to start visualizing your brand's structure
            </p>
            <button
              onClick={handleNewSitemap}
              disabled={isCreating}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors bg-workspace-accent hover:bg-workspace-accent-hover text-white shadow-sm disabled:opacity-60"
            >
              {isCreating ? <LoadingSpinner className="text-white" /> : <Plus className="w-5 h-5" />}
              <span className="font-medium">New Sitemap</span>
            </button>
          </div>
        </div>
      ) : (
        <section className={viewMode === 'grid' ? "grid gap-5 sm:grid-cols-2 lg:grid-cols-3" : "space-y-3"}>
          {displayedSitemaps.map(sitemap => {
            const pages = sitemap.pages || [];
            const homepage = pages.find((p: any) => p.type === 'home');
            const thumbnailUrl = sitemap.thumbnail_url || homepage?.meta?.featuredImage || null;

            return viewMode === 'grid' ? (
              <motion.div
                key={sitemap.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="group relative rounded-2xl overflow-hidden cursor-pointer bg-card border border-border shadow-sm hover:shadow-md transition-all"
                onClick={() => navigate(`/brand/sitemaps/editor/${sitemap.id}`)}
              >
                {/* Thumbnail */}
                <div className="w-full h-32 flex items-center justify-center bg-muted rounded-b-lg overflow-hidden">
                  {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={sitemap.name} className="w-full h-full object-cover" />
                  ) : (
                    <MapIcon className="w-12 h-12 text-muted-foreground/40" />
                  )}
                </div>

                {/* Card footer */}
                <div className="py-4 px-5 flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-2">
                    {renamingId === sitemap.id ? (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') commitRename(sitemap.id); if (e.key === 'Escape') setRenamingId(null); }}
                          className="flex-1 text-sm font-medium rounded px-2 py-1 border bg-background border-border text-foreground focus:outline-none focus:border-workspace-accent"
                        />
                        <button onClick={() => commitRename(sitemap.id)} className="p-1 rounded hover:bg-accent text-green-500"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setRenamingId(null)} className="p-1 rounded hover:bg-accent text-muted-foreground"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <p className="text-sm font-semibold truncate text-foreground">{sitemap.name}</p>
                    )}
                    <p className="text-xs mt-1 text-muted-foreground">
                      {pages.length} page{pages.length !== 1 ? 's' : ''} · {new Date(sitemap.updated_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Kebab menu */}
                  <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-0 focus-visible:ring-0 outline-none"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => startRename(sitemap)}>
                          <Pencil className="h-4 w-4" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          destructive
                          onClick={() => handleDelete(sitemap.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={sitemap.id}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                className="group flex items-center gap-4 p-4 rounded-2xl cursor-pointer bg-card border border-border shadow-sm hover:shadow-md transition-all"
                onClick={() => navigate(`/brand/sitemaps/editor/${sitemap.id}`)}
              >
                {/* Thumbnail */}
                <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-muted">
                  {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={sitemap.name} className="w-full h-full object-cover" />
                  ) : (
                    <MapIcon className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {renamingId === sitemap.id ? (
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitRename(sitemap.id); if (e.key === 'Escape') setRenamingId(null); }}
                        className="flex-1 text-sm font-medium rounded px-2 py-1 border bg-background border-border text-foreground focus:outline-none"
                      />
                      <button onClick={() => commitRename(sitemap.id)} className="p-1 rounded text-green-500"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setRenamingId(null)} className="p-1 rounded text-muted-foreground"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold truncate text-foreground">{sitemap.name}</p>
                  )}
                  <p className="text-xs mt-0.5 text-muted-foreground">
                    {pages.length} page{pages.length !== 1 ? 's' : ''} · Updated {new Date(sitemap.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-0 focus-visible:ring-0 outline-none"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => startRename(sitemap)}>
                        <Pencil className="h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        destructive
                        onClick={() => handleDelete(sitemap.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            );
          })}
        </section>
      )}
    </div>
  );
}
